// Persona enum mirror of the backend WorkspaceMembership.Persona
// TextChoices. See ADR 0002 (backend repo) — persona drives experience
// routing only; permission decisions never read this value.
//
// Lives in its own file so it can be imported by both useActivePersona
// (the legacy shim) and useWorkspaceVisibility (the foundation hook)
// without forming a circular dependency.

export const PERSONAS = Object.freeze({
  ADMIN: 'admin',
  CONTRIBUTOR: 'contributor',
  SPONSOR: 'sponsor',
  VOLUNTEER: 'volunteer',
  AUDITOR: 'auditor',
  BOARD_MEMBER: 'board_member',
  AGENTIC: 'agentic',
  PRIVATE: 'private'
} as const);

export type PersonaValue = typeof PERSONAS[keyof typeof PERSONAS];

export const VALID_PERSONAS: ReadonlySet<PersonaValue> = new Set(
  Object.values(PERSONAS)
);

// Human labels for the header badge, and anywhere else a persona is shown to
// the person it describes.
//
// Every value in PERSONAS has an entry, and `personaLabel` falls back to the
// raw enum string rather than to nothing — so a persona added to the backend
// enum before this map shows up as `adviser`, obviously a gap, instead of
// silently vanishing from the header. A missing label must never read as a
// missing persona.
//
// A LABEL, NEVER A PERMISSION (ADR 0002). It names which workspace experience
// the viewer is in; it grants nothing, and no gate may read it.
export const PERSONA_LABELS: Readonly<Record<string, string>> = Object.freeze({
  [PERSONAS.ADMIN]: 'Admin',
  [PERSONAS.CONTRIBUTOR]: 'Contributor',
  [PERSONAS.SPONSOR]: 'Sponsor',
  [PERSONAS.VOLUNTEER]: 'Volunteer',
  [PERSONAS.AUDITOR]: 'Auditor',
  [PERSONAS.BOARD_MEMBER]: 'Board member',
  [PERSONAS.AGENTIC]: 'Agentic',
  [PERSONAS.PRIVATE]: 'Private'
});

export const personaLabel = (persona?: string | null): string | null =>
  persona ? PERSONA_LABELS[persona] || persona : null;

// RBAC role values on WorkspaceMembership.role that grant the admin
// experience tier regardless of persona. Mirrors the backend rule at
// components/identity/domain/policies/workspace_role_policy.py
// (_ADMIN_RBAC_ROLES) — see ADR 0002. Without this override, an owner
// or admin whose membership row has stale persona data (e.g. legacy
// invite that defaulted persona='contributor' before the persona-
// coercion fix landed) renders the contributor sidebar even though the
// API ships them the full admin visible_sections.
export const ADMIN_TIER_ROLES: ReadonlySet<string> = new Set([
  'owner',
  'admin'
]);
